import React, { createContext, useContext, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';

import {
  ENDPOINT_QUOTES,
  ENDPOINT_QUOTES_DRAFT,
  ENDPOINT_QUOTES_INSTALLER,
  QUOTE_DEFAULT_TYPES,
  QUOTE_TYPES,
  ROUTE_EDIT,
  ROUTE_QUOTES,
} from '@/common/constants';
import {
  itemProductsMutator,
  itemsProductsMapper,
} from '@/common/hooks/prices-and-stocks/dataMappersAndMutators';
import usePricesAndStocks from '@/common/hooks/prices-and-stocks/usePricesAndStocks';
import useCreateQuoteInstaller from '@/common/mutations/quotes/useCreateQuoteInstaller';
import useSelectAll from '@/common/mutations/quotes/useSelectAll';
import useSelectItem from '@/common/mutations/quotes/useSelectItem';
import useModifyItemOption from '@/common/mutations/quotes/useSelectOption';
import useModifyItemVariant from '@/common/mutations/quotes/useSelectVariant';
import useWorksite from '@/common/queries/worksites/useWorksite';
import useQuote from '@/ebat/hooks/quotes/useQuote';

import {
  QuoteContextProviderDefaultProps,
  QuoteContextProviderPropTypes,
} from './QuoteContextProvider.proptypes';
import useManageQuoteContext from './useManageQuoteContext';

const QuoteContext = createContext(null);

const QuoteContextProvider = ({ children }) => {
  const history = useHistory();
  const { id } = useParams();

  let endPoint = ENDPOINT_QUOTES;

  switch (id.substr(0, 1)) {
    case QUOTE_TYPES.letter.draft:
      endPoint = ENDPOINT_QUOTES_DRAFT;
      break;
    case QUOTE_TYPES.letter.installer:
      endPoint = ENDPOINT_QUOTES_INSTALLER;
      break;
    case QUOTE_TYPES.letter.quote:
    default:
      endPoint = ENDPOINT_QUOTES;
  }

  const queryKey = [endPoint, id];
  usePricesAndStocks(
    queryKey,
    itemsProductsMapper,
    itemProductsMutator,
    endPoint === ENDPOINT_QUOTES_INSTALLER,
  );

  const {
    data: quote,
    isFetching,
    isLoading,
    isError,
    isSuccess,
    refetch,
  } = useQuote(queryKey);
  const formatedQuote = useMemo(() => {
    if (!quote || !quote.quote_lines) {
      return quote;
    }

    const updatedQuoteLines = quote.quote_lines.map((item) => {
      const quoteItem = {
        ...item,
        is_selected: item.is_selected ?? true,
      };

      if (item.type !== QUOTE_DEFAULT_TYPES.PRODUCT) {
        delete quoteItem.is_selected;
      }

      return quoteItem;
    });

    return {
      ...quote,
      quote_lines: updatedQuoteLines,
    };
  }, [quote]);

  const { data: worksite } = useWorksite(quote?.worksite?.code);
  const { formatAddTo } = useManageQuoteContext({
    formatedQuote,
  });

  const { mutate: mutateSelectAll } = useSelectAll(queryKey);
  const selectAll = ({ selected }) => mutateSelectAll({ selected });

  const { mutate: mutateSelectItem } = useSelectItem(queryKey);
  const selectItem = ({ itemId, isSelected }) =>
    mutateSelectItem({ itemId, isSelected });

  const { mutate: mutateSelectOption } = useModifyItemOption(queryKey);
  const selectOption = ({ itemId, quoteLine, isSelected }) =>
    mutateSelectOption({ itemId, quoteLine, isSelected });

  const { mutate: mutateSelectVariant } = useModifyItemVariant(queryKey);
  const selectVariant = ({ itemId, quoteLine, isSelected }) =>
    mutateSelectVariant({ itemId, quoteLine, isSelected });

  const {
    mutate: mutatePostQuotesInstaller,
    isLoading: postQuoteInstallerLoading,
  } = useCreateQuoteInstaller([endPoint]);

  const createQuoteInstaller = ({ reference, source }) => {
    if (formatAddTo && formatAddTo.length > 0) {
      mutatePostQuotesInstaller(
        { reference, source, items: formatAddTo },
        {
          onSuccess: (data) =>
            history.push(`${ROUTE_QUOTES}/${data.id}${ROUTE_EDIT}`),
        },
      );
    }
  };

  const isDisabled = useMemo(() => {
    const loading = isLoading || isFetching || postQuoteInstallerLoading;

    return {
      loading,
    };
  }, [isFetching, isLoading, postQuoteInstallerLoading]);

  return (
    <QuoteContext.Provider
      value={{
        data: formatedQuote,
        createQuoteInstaller,
        formatAddTo,
        isError,
        isFetching,
        isLoading: isDisabled.loading,
        isSuccess,
        postQuoteInstallerLoading,
        refetch,
        selectAll,
        selectItem,
        selectOption,
        selectVariant,
        worksite,
      }}
    >
      {children}
    </QuoteContext.Provider>
  );
};

QuoteContextProvider.defaultProps = {
  ...QuoteContextProviderDefaultProps,
};

QuoteContextProvider.propTypes = {
  ...QuoteContextProviderPropTypes,
};

export const useQuoteContext = () => useContext(QuoteContext);

export default QuoteContextProvider;
